export type SortDirection = 'asc' | 'desc';

/**
 * Minimal interface covering all sortable columns used across quote helper files.
 * Fields are optional (Partial) so the generic can accept any quote type that
 * has a subset of these properties.
 */
interface Sortable {
  quoteNumber: string;
  customerName: string;
  customerEmail: string;
  totalAmount: number;
  status: string;
  createdAt: string;
}

/**
 * Generic sort utility that covers string, number, and date (createdAt) comparisons.
 * Replaces the duplicated sortQuotes functions in every quote-helper module.
 */
export function sortByColumn<T extends Partial<Sortable>>(
  items: T[],
  column: keyof Sortable | null,
  direction: SortDirection
): T[] {
  if (!column) return items;

  return [...items].sort((a, b) => {
    const valA = a[column];
    const valB = b[column];
    let cmp = 0;

    if (typeof valA === 'string' && typeof valB === 'string') {
      cmp = (valA || '').localeCompare(valB || '');
    } else if (typeof valA === 'number' && typeof valB === 'number') {
      cmp = valA - valB;
    } else if (column === 'createdAt') {
      cmp = new Date(valA as string).getTime() - new Date(valB as string).getTime();
    }

    return direction === 'asc' ? cmp : -cmp;
  });
}

/**
 * Convenience type alias — matches the SortColumn shape used in every quote module.
 */
export type SortColumn = keyof Sortable;
